/**
 * RewardClaimsHistory — «Mine bytter» på Premier (#156, steg 6a).
 *
 * Barnets egne reward_claims, nyeste først, som rader i ett glasskort: tittel
 * til venstre og statuschip til høyre («Venter på svar», «Godkjent»,
 * «Ikke nå»). Et avslag får grunnen under tittelen, med navnet til den voksne
 * som avslo foran når det er kjent («Mamma: Ikke denne helga – neste?»).
 *
 * Kravet har bare `reward_id` og `approved_by`, så tittelen og navnet slås opp
 * i oppslagene ruten sender inn (belønningslisten og familiens medlemmer). En
 * belønning som ikke lenger står i listen (arkivert eller filtrert bort) vises
 * som «Belønning»; et ukjent navn utelates, og grunnen står alene.
 *
 * Ruten henter via useMyRewardClaims; useClaimReward legger det nye kravet
 * først i bufferen, så listen oppdateres uten en ny runde mot serveren.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { GLASS_SURFACE } from '../../components/primitives/Card';
import { Pill, type PillVariant } from '../../components/primitives/Pill';
import type { RewardClaimRow } from './useRewardsQueries';

interface RewardClaimsHistoryProps {
  claims: RewardClaimRow[];
  /** reward_id → tittel. */
  rewardTitles: ReadonlyMap<string, string>;
  /** member_id → kortnavn, for den som avslo. */
  memberNames: ReadonlyMap<string, string>;
}

const STATUS_TONE: Record<RewardClaimRow['status'], PillVariant> = {
  pending: 'warn',
  approved: 'ok',
  rejected: 'neutral',
};

export function RewardClaimsHistory({
  claims,
  rewardTitles,
  memberNames,
}: RewardClaimsHistoryProps): ReactNode {
  const { t } = useTranslation();

  if (claims.length === 0) {
    return (
      <p data-testid="reward-claims-history-empty" className="m-0 text-sm text-ink-2">
        {t('rewards.history.empty')}
      </p>
    );
  }

  return (
    <ul
      data-testid="reward-claims-history"
      className={`${GLASS_SURFACE} m-0 flex list-none flex-col rounded-row px-3.5 py-1`}
    >
      {claims.map((claim) => {
        const reason = claim.status === 'rejected' ? claim.rejected_reason : null;
        const by = claim.approved_by ? memberNames.get(claim.approved_by) : undefined;
        return (
          <li
            key={claim.id}
            data-testid="reward-claim-row"
            className="flex min-h-13 items-center gap-3 border-b border-card-line py-2 last:border-b-0"
          >
            <span className="min-w-0 flex-1">
              <span className="block text-body-s font-bold text-ink">
                {rewardTitles.get(claim.reward_id) ?? t('rewards.history.unknownReward')}
              </span>
              {reason ? (
                <span data-testid="reward-claim-reason" className="block text-xs text-ink-2">
                  {by ? t('rewards.history.reasonBy', { name: by, reason }) : reason}
                </span>
              ) : null}
            </span>
            <span data-testid="reward-claim-status" data-status={claim.status} className="shrink-0">
              <Pill variant={STATUS_TONE[claim.status]}>
                {t(`rewards.history.status.${claim.status}`)}
              </Pill>
            </span>
          </li>
        );
      })}
    </ul>
  );
}
